import type { MnrqSeasonRule } from '@/lib/mnrq/types';
import type { Promotion } from '@/lib/types';

function zonedDateParts(date: Date, timezone: string) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(date);
  const map = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
  };
}

function monthDayValue(month: number, day: number) {
  return month * 100 + day;
}

function isDateOnlyValue(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

function parsePromotionBoundary(
  value: string | undefined,
  timezone: string,
  boundary: 'start' | 'end',
) {
  if (!value) return undefined;
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (!isDateOnlyValue(trimmed)) {
    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }

  const [yearRaw, monthRaw, dayRaw] = trimmed.split('-');
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return undefined;
  }

  const hour = boundary === 'start' ? 0 : 23;
  const minute = boundary === 'start' ? 0 : 59;
  const second = boundary === 'start' ? 0 : 59;
  const millisecond = boundary === 'start' ? 0 : 999;
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, second, millisecond));
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  for (let offsetHours = -36; offsetHours <= 36; offsetHours += 1) {
    const candidate = new Date(utcGuess.getTime() - offsetHours * 60 * 60 * 1000);
    const rendered = formatter.format(candidate);
    if (rendered === trimmed) {
      return candidate;
    }
  }

  return utcGuess;
}

export function isSeasonRuleActive(rule: MnrqSeasonRule, referenceDate = new Date()): boolean {
  const current = zonedDateParts(referenceDate, rule.timezone);
  const nowValue = monthDayValue(current.month, current.day);
  const startValue = monthDayValue(rule.startMonth, rule.startDay);
  const endValue = monthDayValue(rule.endMonth, rule.endDay);

  if (startValue <= endValue) {
    return nowValue >= startValue && nowValue <= endValue;
  }

  // Handles cross-year seasons such as Nov-Feb.
  return nowValue >= startValue || nowValue <= endValue;
}

export function pickActiveSeasonRule(rules: MnrqSeasonRule[], referenceDate = new Date()): MnrqSeasonRule | undefined {
  return rules
    .filter((rule) => isSeasonRuleActive(rule, referenceDate))
    .sort((a, b) => (a.priority || 100) - (b.priority || 100))[0];
}

export function isPromotionActive(promotion: Promotion, referenceDate = new Date()): boolean {
  if (!promotion.is_active) {
    return false;
  }

  const timezone = promotion.timezone || 'UTC';

  if (promotion.start_date) {
    const startsAt = parsePromotionBoundary(promotion.start_date, timezone, 'start');
    if (startsAt && startsAt.getTime() > referenceDate.getTime()) {
      return false;
    }
  }

  if (promotion.end_date) {
    const endsAt = parsePromotionBoundary(promotion.end_date, timezone, 'end');
    if (endsAt && referenceDate.getTime() > endsAt.getTime()) {
      return false;
    }
  }

  const schedule = promotion.schedule_rules;
  if (!schedule || typeof schedule !== 'object') {
    return true;
  }

  const current = zonedDateParts(referenceDate, timezone);
  const windows = (schedule as Record<string, unknown>).windows;
  if (!Array.isArray(windows)) {
    return true;
  }

  const currentDay = referenceDate.toLocaleDateString('en-US', { weekday: 'long', timeZone: timezone }).toLowerCase();

  // Convert to minutes-since-midnight (0–1439) for comparison.
  // The old approach used a numeric HHMM format (e.g. 1430 for 14:30) which
  // breaks for windows that cross midnight: {start: 2200, end: 200} can never
  // satisfy `currentMinutes >= 2200 && currentMinutes <= 200` because no
  // integer satisfies both sides simultaneously.
  const timeParts = referenceDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: timezone,
  }).split(':');
  const currentMinutes = Number(timeParts[0] || 0) * 60 + Number(timeParts[1] || 0);

  function parseWindowMinutes(value: unknown): number | undefined {
    if (typeof value !== 'string') return undefined;
    const parts = value.replace(':', ' ').trim().split(/\s+/);
    if (parts.length === 2) {
      const h = Number(parts[0]);
      const m = Number(parts[1]);
      if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
    }
    // Fallback: plain numeric like "2200" → 22*60+0=1320, "200" → 2*60+0=120
    const raw = Number(value.replace(':', ''));
    if (!Number.isFinite(raw)) return undefined;
    return Math.floor(raw / 100) * 60 + (raw % 100);
  }

  return windows.some((windowRule) => {
    if (!windowRule || typeof windowRule !== 'object') {
      return false;
    }

    const rule = windowRule as Record<string, unknown>;
    const days = Array.isArray(rule.days) ? (rule.days as string[]).map((day) => day.toLowerCase()) : [];
    const startMins = parseWindowMinutes(rule.start);
    const endMins = parseWindowMinutes(rule.end);

    if (days.length && !days.includes(currentDay)) {
      return false;
    }

    if (startMins !== undefined && endMins !== undefined) {
      if (startMins <= endMins) {
        // Normal window: e.g. 09:00–22:00
        return currentMinutes >= startMins && currentMinutes <= endMins;
      }
      // Cross-midnight window: e.g. 22:00–02:00
      return currentMinutes >= startMins || currentMinutes <= endMins;
    }

    return true;
  });
}
